import React, { useState, useEffect } from 'react';
import { Cpu, Plus, Send, PanelLeft, Loader2 } from 'lucide-react';
import { askAiAgentApi } from '../services/axios/AuthApis/AIAgentApis';

const STORAGE_KEY = 'aiAssistantHistory';
const ACTIVE_KEY = 'aiAssistantActiveId';

const loadStoredHistory = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) && parsed.length > 0
      ? parsed
      : [{ id: 1, title: 'New Chat', messages: [] }];
  } catch {
    return [{ id: 1, title: 'New Chat', messages: [] }];
  }
};

const loadStoredActiveId = (history) => {
  try {
    const raw = localStorage.getItem(ACTIVE_KEY);
    const id = raw ? Number(raw) : null;
    return id && history.some((h) => h.id === id) ? id : history[0].id;
  } catch {
    return history[0].id;
  }
};

const AIAgentPage = () => {
  const [history, setHistory] = useState(loadStoredHistory);
  const [activeId, setActiveId] = useState(() => loadStoredActiveId(loadStoredHistory()));
  const [input, setInput] = useState('');
  const [showHistory, setShowHistory] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
    } catch {
      // ignore storage failures (private mode, quota, etc.)
    }
  }, [history]);

  useEffect(() => {
    try {
      localStorage.setItem(ACTIVE_KEY, String(activeId));
    } catch {
      // ignore
    }
  }, [activeId]);

  const activeChat = history.find((h) => h.id === activeId);

  const handleNewChat = () => {
    const id = Date.now();
    setHistory((prev) => [{ id, title: 'New Chat', messages: [] }, ...prev]);
    setActiveId(id);
  };

  const updateChat = (chatId, updater) => {
    setHistory((prev) => prev.map((h) => (h.id === chatId ? updater(h) : h)));
  };

  const handleSend = async () => {
    const question = input.trim();
    if (!question || loading) return;
    const chatId = activeId;

    updateChat(chatId, (h) => ({
      ...h,
      title: h.messages.length === 0 ? question.slice(0, 40) : h.title,
      messages: [...h.messages, { role: 'user', text: question }],
    }));
    setInput('');
    setLoading(true);

    try {
      const { answer } = await askAiAgentApi(question);
      updateChat(chatId, (h) => ({
        ...h,
        messages: [...h.messages, { role: 'assistant', text: answer || 'No answer was returned.' }],
      }));
    } catch (err) {
      const text = err?.response?.data?.detail || 'The AI agent could not answer right now.';
      updateChat(chatId, (h) => ({
        ...h,
        messages: [...h.messages, { role: 'assistant', text, isError: true }],
      }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-[calc(100vh-4rem)]">
      {/* History sidebar */}
      {/* {showHistory && (
        <div className="w-64 shrink-0 border-r border-gray-200 bg-gray-50 flex flex-col">
          <div className="p-3 border-b border-gray-200">
            <button
              onClick={handleNewChat}
              className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              New Chat
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {history.map((h) => (
              <button
                key={h.id}
                onClick={() => setActiveId(h.id)}
                className={`w-full text-left px-3 py-2 text-sm truncate transition-colors border-b-2 ${
                  h.id === activeId
                    ? "border-blue-600 text-blue-700 font-medium"
                    : "border-transparent text-gray-700 hover:bg-gray-100"
                }`}
                title={h.title}
              >
                {h.title}
              </button>
            ))}
          </div>
        </div>
      )} */}

      {/* Chat panel */}
      <div className="flex-1 flex flex-col bg-white">
        {/* <div className="p-3 border-b border-gray-200">
          <button
            onClick={() => setShowHistory((v) => !v)}
            className="p-2 rounded-lg text-gray-600 hover:bg-gray-100"
            title={showHistory ? "Hide history" : "Show history"}
          >
            <PanelLeft className="w-5 h-5" />
          </button>
        </div> */}
        {!activeChat || activeChat.messages.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
            <div className="p-4 bg-blue-50 rounded-full mb-4">
              <Cpu className="w-10 h-10 text-blue-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">AI Assistant</h2>
            <p className="text-sm text-gray-500 mt-1 max-w-md">
              Start a conversation below.
            </p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {activeChat.messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[70%] px-4 py-2 rounded-2xl text-sm break-words ${
                    m.role === 'user'
                      ? 'bg-blue-600 text-white'
                      : m.isError
                      ? 'bg-red-50 text-red-700 border border-red-200'
                      : 'bg-gray-100 text-black'
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="px-4 py-2 rounded-2xl bg-gray-100 text-black">
                  <Loader2 className="w-4 h-4 animate-spin" />
                </div>
              </div>
            )}
          </div>
        )}

        <div className="border-t border-gray-200 p-4">
          <div className="flex items-center gap-2 max-w-3xl mx-auto">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Message AI Assistant..."
              className="flex-1 px-4 py-2.5 text-sm border border-gray-300 rounded-full outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleSend}
              className="p-2.5 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-50"
              disabled={!input.trim() || loading}
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAgentPage;
